// SCRUM-1850 (SCRUM-1831 slice 4): an administrator sets a category's product
// score configuration — criteria rows (property, direction, weight), checked
// against real products with Preview before Save. The routes and their rules
// (weights sum to 100, admin-only save, no-write preview) are SCRUM-1847's; this
// screen only shows them. Local state, the LabelCategories shape.
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Container, Input, Table } from "reactstrap";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { getCategories } from "@/services/categoryServices/categoryAction";
import { getAllProperties } from "@/services/productServices/productActions";
import { getSites } from "@/services/siteServices/siteActions";
import {
  getScoreConfig,
  previewScores,
  saveScoreConfig,
} from "@/services/scoreConfigServices/scoreConfigApi";

import type { ScoreCriterion, ScorePreviewRow } from "@/services/scoreConfigServices/scoreConfigApi";

const DIRECTIONS = ["lower", "higher", "boolean"];
/** One empty list, so a missing slice does not change the memo inputs every render. */
const NONE: never[] = [];

/** A criterion row plus a client-only id, so React keys a row on itself and not on its position. */
type Row = ScoreCriterion & { uid?: number };
let lastUid = 0;
const withUid = (criterion: ScoreCriterion): Row => ({ ...criterion, uid: ++lastUid });
/** A row as the server stores it: the client id never leaves this screen. */
const toCriterion = (row: Row): ScoreCriterion => {
  const criterion: Row = { ...row };
  delete criterion.uid;
  return criterion;
};

const emptyRow = (): Row => withUid({ property: "", direction: "higher", weight: 0 });

/** The first message of a `{ errors: [{ msg }] }` answer, else a generic one. */
const refusalOf = (err) =>
  err?.response?.data?.errors?.[0]?.msg || err?.message || "The configuration could not be saved.";

/**
 * The property ids the category's subcategories carry, over every country entry
 * (`properties: [{ key, value: [ids] }]`), plus the ids already configured.
 */
const categoryPropertyIds = (category: any, rows: ScoreCriterion[]): Set<string> => {
  const ids = new Set(rows.map((r) => r.property).filter(Boolean));
  (category?.subcategories || []).forEach((sub) =>
    (sub?.properties || []).forEach((entry) =>
      (Array.isArray(entry?.value) ? entry.value : []).forEach((id) => ids.add(String(id))),
    ),
  );
  return ids;
};

const ScoreConfig = () => {
  const { id } = useParams();
  const categoryId = String(id);
  const dispatch = useAppDispatch();
  const categories = (useAppSelector((s) => s.categories?.categories) || NONE) as any[];
  const properties = (useAppSelector((s) => s.products?.properties) || NONE) as any[];
  const sites = (useAppSelector((s) => s.sites?.sites) || NONE) as any[];
  const currentSiteId = useAppSelector((s) => s.sites?.site?._id);

  const [rows, setRows] = useState<Row[]>(() => [emptyRow()]);
  const [siteId, setSiteId] = useState("");
  const [preview, setPreview] = useState<ScorePreviewRow[] | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    dispatch(getCategories());
    dispatch(getAllProperties());
    if (sites.length === 0) dispatch(getSites());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per category; sites.length is filled by other screens and must not refetch
  }, [dispatch, categoryId]);

  useEffect(() => {
    let cancelled = false;
    getScoreConfig(categoryId)
      .then((config) => {
        if (!cancelled && config?.criteria?.length) setRows(config.criteria.map(withUid));
      })
      .catch((err) => {
        if (!cancelled) setError(refusalOf(err));
      });
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  useEffect(() => {
    if (!siteId && currentSiteId) setSiteId(String(currentSiteId));
  }, [siteId, currentSiteId]);

  const category = categories.find((c) => String(c?._id) === categoryId);
  const options = useMemo(() => {
    const ids = categoryPropertyIds(category, rows);
    return properties.filter((p) => ids.has(String(p?._id)));
  }, [category, properties, rows]);

  const total = rows.reduce((sum, r) => sum + (Number(r.weight) || 0), 0);
  const complete = rows.length > 0 && rows.every((r) => r.property);
  const canSave = complete && Math.abs(total - 100) < 1e-9;

  const updateRow = (index: number, change: Partial<ScoreCriterion>) => {
    setSaved(false);
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...change } : r)));
  };

  const onPreview = async () => {
    setError("");
    try {
      setPreview(await previewScores(categoryId, siteId, rows.map(toCriterion)));
    } catch (err) {
      setError(refusalOf(err));
    }
  };

  const onSave = async () => {
    setError("");
    try {
      await saveScoreConfig(categoryId, rows.map(toCriterion));
      setSaved(true);
    } catch (err) {
      setError(refusalOf(err));
    }
  };

  return (
    <>
      <SimpleHeader name="Scoring" parentName="Categories" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader>
            <h3 className="mb-0">Product score — {category?.name || categoryId}</h3>
          </CardHeader>
          <CardBody>
            {rows.map((row, i) => (
              <div className="d-flex mb-2" key={row.uid} data-testid={`score-row-${i}`}>
                <Input type="select" aria-label={`Property ${i + 1}`} value={row.property}
                  onChange={(e) => updateRow(i, { property: e.target.value })}>
                  <option value="">Choose a property</option>
                  {/* A property chosen in another row is not offered again (SCRUM-1945), but a
                      row always keeps its own value, also when a stored config repeats it. */}
                  {options.filter((p) => String(p._id) === row.property || !rows.some((r, j) => j !== i && r.property === String(p._id))).map((p) => (
                    <option key={p._id} value={String(p._id)}>{p.name}</option>
                  ))}
                </Input>
                <Input type="select" aria-label={`Direction ${i + 1}`} value={row.direction} className="mx-2"
                  onChange={(e) => updateRow(i, { direction: e.target.value as ScoreCriterion["direction"] })}>
                  {DIRECTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </Input>
                <Input type="number" min="0" aria-label={`Weight ${i + 1}`} value={row.weight}
                  onChange={(e) => updateRow(i, { weight: Number(e.target.value) })} />
                <Button color="danger" size="sm" className="ml-2" aria-label={`Remove row ${i + 1}`}
                  onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}>×</Button>
              </div>
            ))}
            <Button size="sm" onClick={() => setRows((prev) => [...prev, emptyRow()])}>Add criterion</Button>
            <p className="mt-3" data-testid="score-total">Total weight: {total}{canSave ? "" : " (must be 100)"}</p>

            <div className="d-flex align-items-center mb-3">
              <Input type="select" aria-label="Site" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                style={{ maxWidth: 320 }}>
                <option value="">Choose a site</option>
                {sites.map((s) => <option key={s._id} value={String(s._id)}>{s.name || s.domain}</option>)}
              </Input>
              <Button color="info" className="ml-2" disabled={!complete || !siteId} onClick={onPreview}>Preview</Button>
              <Button color="primary" className="ml-2" disabled={!canSave} onClick={onSave}>Save</Button>
            </div>

            {error && <p className="text-danger" role="alert">{error}</p>}
            {saved && <p className="text-success">Saved.</p>}

            {preview && (
              <Table size="sm" data-testid="score-preview">
                <thead>
                  <tr><th>Product</th><th>Score</th><th>Parts</th></tr>
                </thead>
                <tbody>
                  {preview.map((p) => (
                    <tr key={p.product} data-testid={`preview-row-${p.product}`}>
                      <td>{p.title || p.product}</td>
                      <td>{p.score === null ? p.reason : p.score}</td>
                      <td>{(p.parts || []).map((part) =>
                        `${properties.find((x) => String(x?._id) === String(part.property))?.name || part.property}: ${part.partScore ?? "–"} × ${part.weight}`,
                      ).join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default ScoreConfig;
